import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Users, Briefcase } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, RevealImage } from "../components/Reveal";
import { IMAGES, services, fleet, destinations } from "../lib/data";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex h-screen min-h-[640px] items-center overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="Luxury car" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-matte" />
      </motion.div>

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-7xl px-5 lg:px-8">
        <Reveal delay={0.1}>
          <span className="font-body text-xs uppercase tracking-[0.3em] text-gold">{t("hero.tag")}</span>
        </Reveal>
        <div className="mt-6 max-w-4xl">
          <Reveal delay={0.2}>
            <h1 className="font-heading text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl">
              {t("hero.title1")} <br />
              <span className="text-gradient-gold italic">{t("hero.title2")}</span>
            </h1>
          </Reveal>
        </div>
        <Reveal delay={0.35}>
          <p className="mt-8 max-w-xl font-body text-base leading-relaxed text-white/70 md:text-lg">{t("hero.subtitle")}</p>
        </Reveal>
        <Reveal delay={0.5}>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to="/booking" data-testid="hero-book-btn"
              className="group flex items-center justify-center gap-2 bg-gold px-8 py-4 font-body text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-muted">
              {t("hero.cta")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" data-testid="hero-services-btn"
              className="flex items-center justify-center gap-2 border border-white/25 px-8 py-4 font-body text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-gold">
              {t("hero.cta2")}
            </Link>
          </div>
        </Reveal>
      </motion.div>
    </section>
  );
};

const stats = [
  { value: "2K+", key: "home.statsClients" },
  { value: "12+", key: "home.statsYears" },
  { value: "30+", key: "home.statsCities" },
  { value: "4.9★", key: "home.statsRating" },
];

const Intro = () => {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
        <div>
          <Reveal><span className="font-body text-xs uppercase tracking-[0.3em] text-gold">{t("home.introTag")}</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-tight text-white lg:text-5xl">{t("home.introTitle")}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 font-body text-base leading-relaxed text-white/60">{t("home.introText")}</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 gap-6 self-center">
          {stats.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.1} className="border border-white/10 bg-matte-surface p-8">
              <div className="font-heading text-4xl text-gold lg:text-5xl">{s.value}</div>
              <div className="mt-2 font-body text-xs uppercase tracking-widest text-white/50">{t(s.key)}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesPreview = () => {
  const { t, lang } = useLang();
  return (
    <section className="border-t border-white/5 bg-matte-deep py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal><span className="font-body text-xs uppercase tracking-[0.3em] text-gold">{t("home.servicesTag")}</span></Reveal>
            <Reveal delay={0.1}><h2 className="mt-4 max-w-lg font-heading text-4xl font-medium text-white lg:text-5xl">{t("home.servicesTitle")}</h2></Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/services" data-testid="home-services-all" className="group flex items-center gap-2 font-body text-xs uppercase tracking-[0.2em] text-gold">
              {t("common.viewAll")} <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.slice(0, 4).map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Star;
            return (
              <Reveal key={s.key} delay={i * 0.08} className="group border border-white/10 bg-matte-surface p-8 transition-colors duration-500 hover:border-gold/40">
                <Icon size={28} className="text-gold" strokeWidth={1.4} />
                <h3 className="mt-6 font-display text-xl text-white">{s[lang].title}</h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-white/50">{s[lang].desc}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FleetPreview = () => {
  const { t, lang } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <div className="text-center">
        <Reveal><span className="font-body text-xs uppercase tracking-[0.3em] text-gold">{t("home.fleetTag")}</span></Reveal>
        <Reveal delay={0.1}><h2 className="mx-auto mt-4 max-w-2xl font-heading text-4xl font-medium text-white lg:text-5xl">{t("home.fleetTitle")}</h2></Reveal>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {fleet.map((f, i) => (
          <Reveal key={f.key} delay={i * 0.1} className="group overflow-hidden border border-white/10 bg-matte-surface">
            <RevealImage src={f.image} alt={f[lang].name} className="h-64" imgClassName="transition-transform duration-700 group-hover:scale-105" />
            <div className="p-7">
              <span className="font-body text-[11px] uppercase tracking-[0.2em] text-gold">{f[lang].tag}</span>
              <h3 className="mt-2 font-display text-2xl text-white">{f[lang].name}</h3>
              <div className="mt-4 flex gap-6 font-body text-sm text-white/60">
                <span className="flex items-center gap-2"><Users size={16} className="text-gold" /> {f.seats}</span>
                <span className="flex items-center gap-2"><Briefcase size={16} className="text-gold" /> {f.bags}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const DestinationsPreview = () => {
  const { t, lang } = useLang();
  return (
    <section className="border-t border-white/5 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal><span className="font-body text-xs uppercase tracking-[0.3em] text-gold">{t("home.destTag")}</span></Reveal>
        <Reveal delay={0.1}><h2 className="mt-4 max-w-2xl font-heading text-4xl font-medium text-white lg:text-5xl">{t("home.destTitle")}</h2></Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {destinations.map((d, i) => (
            <Reveal key={d.key} delay={i * 0.08} className="group relative h-96 overflow-hidden">
              <img src={d.image} alt={d[lang].name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="font-display text-2xl text-white">{d[lang].name}</h3>
                <p className="mt-2 font-body text-sm text-white/60">{d[lang].desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden">
      <img src={IMAGES.interior} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-black/75" />
      <div className="relative mx-auto max-w-4xl px-5 py-28 text-center lg:px-8">
        <Reveal><h2 className="font-heading text-4xl font-medium text-white lg:text-6xl">{t("home.ctaTitle")}</h2></Reveal>
        <Reveal delay={0.1}><p className="mx-auto mt-6 max-w-lg font-body text-white/60">{t("home.ctaText")}</p></Reveal>
        <Reveal delay={0.2}>
          <Link to="/booking" data-testid="cta-band-book"
            className="mt-10 inline-flex items-center gap-2 bg-gold px-10 py-4 font-body text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-muted">
            {t("common.bookNow")} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Intro />
      <ServicesPreview />
      <FleetPreview />
      <DestinationsPreview />
      <CtaBand />
    </>
  );
}
